import { queryOptions, type QueryClient } from '@tanstack/react-query'
import { repositories } from '@/data'
import type {
  ConnectionStatus,
  ConnectionSummary,
  OAuthPlatform,
} from '@/data/adapters/api/oauth.api'
import { WORKFLOW_MANIFESTS } from './workflow-manifests'

export const CONNECTION_STALE_TIME = 5 * 60 * 1000
export const OAUTH_PLATFORMS: OAuthPlatform[] = ['tiktok_shop', 'tiktok_business']

export type ConnectionHealth =
  | 'ready'
  | 'missing'
  | 'setup-required'
  | 'invalid'
  | 'unknown'

export type BrokenConnectionHealth = Exclude<ConnectionHealth, 'ready' | 'unknown'>

export interface ConnectionAccess {
  connections: ConnectionSummary[]
  byPlatform: Record<OAuthPlatform, ConnectionHealth>
}

export interface RouteConnectionPolicy {
  feature: string
  label: string
  required: OAuthPlatform[]
  optional: OAuthPlatform[]
}

const EXTRA_ROUTE_POLICIES: Record<string, RouteConnectionPolicy> = {
  blueprint: {
    feature: 'blueprint',
    label: 'GMV Blueprint',
    required: ['tiktok_shop'],
    optional: [],
  },
  signal: {
    feature: 'signal',
    label: 'Signal',
    required: ['tiktok_shop'],
    optional: [],
  },
  'campaign-report': {
    feature: 'campaign-report',
    label: 'Campaign report',
    required: ['tiktok_shop'],
    optional: ['tiktok_business'],
  },
}

export const ROUTE_CONNECTION_POLICIES: Record<string, RouteConnectionPolicy> = {
  ...Object.fromEntries(
    WORKFLOW_MANIFESTS.filter((item) => item.connection).map((item) => [
      item.path,
      {
        feature: item.id,
        label: item.label,
        required: item.connection?.required ?? [],
        optional: item.connection?.optional ?? [],
      },
    ]),
  ),
  ...EXTRA_ROUTE_POLICIES,
}

export function classifyConnection(
  summary: ConnectionSummary | undefined,
  status: ConnectionStatus | null | undefined,
): ConnectionHealth {
  if (!summary?.isActive) return 'missing'
  if (summary.needsSetup) return 'setup-required'
  if (status === null) return 'unknown'
  if (!status || !status.connected || status.valid === false) return 'invalid'
  return 'ready'
}

export const connectionAccessKey = (storeId: number) =>
  ['connection-access', storeId] as const

export function connectionAccessQueryOptions(storeId: number) {
  return queryOptions({
    queryKey: connectionAccessKey(storeId),
    staleTime: CONNECTION_STALE_TIME,
    queryFn: async (): Promise<ConnectionAccess> => {
      const connections = await repositories.oauth.listConnections(storeId)
      const summaries = new Map(connections.map((connection) => [connection.platform, connection]))
      const statusEntries = await Promise.all(
        OAUTH_PLATFORMS.map(async (platform) => {
          const summary = summaries.get(platform)
          if (!summary?.isActive || summary.needsSetup) return [platform, undefined] as const
          try {
            return [platform, await repositories.oauth.getStatus(platform, storeId)] as const
          } catch {
            return [platform, null] as const
          }
        }),
      )
      const statuses = new Map(statusEntries)
      return {
        connections,
        byPlatform: {
          tiktok_shop: classifyConnection(summaries.get('tiktok_shop'), statuses.get('tiktok_shop')),
          tiktok_business: classifyConnection(
            summaries.get('tiktok_business'),
            statuses.get('tiktok_business'),
          ),
        },
      }
    },
  })
}

export async function invalidateConnectionAccess(queryClient: QueryClient, storeId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: connectionAccessKey(storeId) }),
    queryClient.invalidateQueries({ queryKey: ['oauth-connections', storeId] }),
    queryClient.invalidateQueries({ queryKey: ['features'] }),
  ])
}

export function routeConnectionPolicy(pathname: string, clientSlug: string) {
  const segments = pathname.split('/').filter(Boolean)
  const decoded = segments.map((segment) => {
    try {
      return decodeURIComponent(segment)
    } catch {
      return segment
    }
  })
  const clientIndex = decoded[1] === clientSlug ? 1 : decoded.indexOf(clientSlug)
  if (clientIndex < 0) return undefined
  const segment = segments[clientIndex + 1]
  return segment ? ROUTE_CONNECTION_POLICIES[segment] : undefined
}

export function firstBrokenRequiredConnection(
  policy: RouteConnectionPolicy,
  access: ConnectionAccess,
): { platform: OAuthPlatform; reason: BrokenConnectionHealth } | undefined {
  for (const platform of policy.required) {
    const reason = access.byPlatform[platform]
    if (reason !== 'ready' && reason !== 'unknown') return { platform, reason }
  }
  return undefined
}

export function isPlatformAvailable(access: ConnectionAccess | undefined, platform: OAuthPlatform) {
  const health = access?.byPlatform[platform]
  return health === undefined || health === 'ready' || health === 'unknown'
}

export function isSafeTenantNext(next: unknown, orgSlug: string, clientSlug: string): next is string {
  if (typeof next !== 'string') return false
  const prefix = `/${encodeURIComponent(orgSlug)}/${encodeURIComponent(clientSlug)}`
  if (next !== prefix && !next.startsWith(`${prefix}/`) && !next.startsWith(`${prefix}?`)) return false
  try {
    const parsed = new URL(next, 'https://scc.local')
    return parsed.origin === 'https://scc.local' && !parsed.pathname.endsWith('/connection')
  } catch {
    return false
  }
}

export const platformLabel = (platform: OAuthPlatform) =>
  platform === 'tiktok_shop' ? 'TikTok Shop' : 'TikTok Ads Manager'
